import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { computePosition } from "@floating-ui/dom";
import { useMediaQuery } from "@mui/material";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { mixHighlightColour } from "../../design/colours";
import { trpc } from "../../utils/trpc";
import { AnnotationMenu } from "../bits/AnnotationMenu";
import { BookmarkButton } from "../bits/bookmarks/BookmarkButton";
import { Chunk } from "../bits/ChunkDisplay";
import { publicationCSS } from "../bits/cssHelpers";
import { LoadingSpinner } from "../bits/loading";
import type { PageColourOptions } from "../bits/settings/defaults";
import { pageColourOptions } from "../bits/settings/defaults";
import { PageWithSettings } from "../bits/settings/PageWithSettings";
import { AnnotationContext } from "../contexts/WithAnnotations/AnnotationContext";
import { PublicationContext } from "../contexts/WithPublication/PublicationContext";
import { SettingsContext } from "../contexts/WithSettings/SettingsContext";
import { useBookmarks } from "../hooks/useBookmarks";
import { useHighlightObserver } from "../hooks/useHighlightObserver";
import { useTextHighlight } from "../hooks/useHighlights";
import { useSearchHighlight } from "../hooks/useSearchHighlights";
import { useTitle } from "../hooks/useTitle";
import { useUser } from "../hooks/useUser";
import {
  cleanSnippet,
  clusterMenus,
  debounce,
  extractChapterKey,
  getAnnotationId,
  getParentElementWithId,
  getRangeFromSnippet,
  getSectionFromContents,
  isElementInViewport,
} from "../utils";
import {
  useHighlightMenu,
  usePageStyles,
  useResizeScrollPreservation,
  useScrollSectionTracker,
} from "../hooks/useReaderPage";

const PublicationWrapper = styled.div<{
  options?: PageColourOptions;
}>`
  ${({ theme }) => theme.layers.relief.accent_20.noShadow};
  ${({ options }) =>
    options &&
    `
      background-color: ${options.background};
      color: ${options.text};

      a {
        color: ${options.link_text};
      }
    `}
`;

const PublicationBody = styled.div<{
  options?: PageColourOptions;
}>`
  max-width: ${({ theme }) => theme.layout.contentMaxWidth};
  margin: 0 auto;

  ${({ options }) => publicationCSS(options)}

  ::selection {
    background: transparent;
  }
  blockquote:has(+ h1),
  ul:has(+ h1),
  p:has(+ h1) {
    padding-bottom: 11rem;
  }
  table:has(+ h1) {
    margin-bottom: 11rem;
  }
  h1 + h1 {
    padding-top: 3rem;
  }
`;

export const Publication = () => {
  const id = "publication-style";
  const rangeRef = useRef<Range>(null);
  const {
    annotationsOpen,
    focusedId,
    setFocusedId,
    setAnnotationsOpen,
    didTapMenuRef,
  } = useContext(AnnotationContext);
  const { ereaderSettings } = useContext(SettingsContext);
  const { user } = useUser();

  const pageColourOption = useMemo(
    () =>
      ereaderSettings?.pageColour
        ? pageColourOptions[
            ereaderSettings?.pageColour as keyof typeof pageColourOptions
          ]
        : undefined,
    [ereaderSettings?.pageColour],
  );

  const {
    styles,
    htmlChunks,
    chapters,
    currentChunk,
    selectSection,
    currentSection,
    contents,
    scrollDisabled,
    setScrollDisabled,
    publicationSlug,
    publication,
    pendingHash,
    setPendingHash,
    scrollSnippet,
    setScrollSnippet,
    containerRef,
  } = useContext(PublicationContext);

  const { portalTargets } = useBookmarks({
    contents,
    containerRef,
    htmlChunks,
  });

  const {
    applyHighlight,
    applyHighlights,
    serializeRange,
    deserializeRange,
    clearHighlight,
    clearBorder,
    applyBorder,
  } = useTextHighlight(containerRef, pageColourOption?.background);

  const theme = useTheme();
  const isLarge = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.lg}px)`,
  );

  const colour = useMemo(
    () =>
      mixHighlightColour(
        pageColourOption?.background || theme.colours.accent.light._15,
        theme.colours.highlight.apple,
      ),
    [
      pageColourOption?.background,
      theme.colours.accent.light._15,
      theme.colours.highlight.apple,
    ],
  );
  const {
    applyHighlight: applyHighlightForSnippet,
    clearHighlight: clearHighlightForSnippet,
    reapplyHighlight: reapplyHighlightForSnippet,
  } = useSearchHighlight(containerRef, containerRef, colour);

  useTitle(`${publication.title} | Compass`);

  const { annotations } = useContext(AnnotationContext);

  const [selectedChapter, setSelectedChapter] = useState<string>();
  const [annotationPositions, setAnnotationPositions] = useState<
    Record<string, { x: number; y: number }>
  >({});

  usePageStyles(styles, id);
  const { currentChunkRef, resizeTimerRef } = useResizeScrollPreservation(
    currentChunk,
    currentSection,
  );
  const {
    annotationSaved,
    setAnnotationSaved,
    currentHighlightMenuOpen,
    setCurrentHighlightMenuOpen,
    annotationPosition,
    setAnnotationPosition,
    currentAnnotation,
    setCurrentAnnotation,
    closeCurrentHighlightMenu,
  } = useHighlightMenu({
    clearHighlight,
    containerRef,
    htmlChunks,
    currentSection,
  });
  useScrollSectionTracker({
    id,
    slug: publicationSlug,
    contents,
    currentSection,
    selectSection,
    scrollDisabled,
    resizeTimerRef,
    closeCurrentHighlightMenu,
  });

  const updateAnnotationMenuPositions = useCallback(async () => {
    if (!annotations || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const rawPositions: { id: string; x: number; y: number }[] = [];
    const rawPositionsSm: { id: string; x: number; y: number }[] = [];

    for (const ann of annotations) {
      const annotationId = getAnnotationId(ann);
      const highlightEl = document.getElementById(`highlight-${annotationId}`);
      const refEl = document.getElementById(ann.range.startNodeId);
      const floatEl = document.getElementById(`ann${ann.id}`);
      const floatElSm = document.getElementById(`sm-${ann.id}`);

      const highlightTop = highlightEl
        ? highlightEl.getBoundingClientRect().top -
          containerRect.top +
          window.scrollY +
          5
        : undefined;

      if (refEl && floatEl && currentChunkRef.current) {
        const chunkContentEl = currentChunkRef.current
          .firstChild as Element | null;
        if (chunkContentEl) {
          const refRect = refEl.getBoundingClientRect();
          const chunkRect = chunkContentEl.getBoundingClientRect();
          const virtualReference = {
            getBoundingClientRect: () =>
              ({
                x: chunkRect.left,
                y: refRect.top,
                top: refRect.top,
                right: chunkRect.right,
                bottom: refRect.bottom,
                left: chunkRect.left,
                width: chunkRect.width,
                height: refRect.height,
              }) as DOMRect,
          };
          const { x, y } = await computePosition(virtualReference, floatEl, {
            placement: "right",
          });
          rawPositions.push({ id: `${ann.id}`, x, y: highlightTop ?? y });
        }
      }

      if (refEl && floatElSm) {
        const { x, y } = await computePosition(refEl, floatElSm, {
          placement: "left",
        });
        rawPositionsSm.push({ id: `sm${ann.id}`, x, y: highlightTop ?? y });
      }
    }

    const clustered = clusterMenus(rawPositions, rawPositionsSm);

    setAnnotationPositions(clustered);
  }, [annotations, containerRef]);

  const saveAnnotation = () => {
    window.getSelection()?.removeAllRanges();
    if (!currentAnnotation || !containerRef.current) return;
    const serializedRange = serializeRange(currentAnnotation);
    if (!serializedRange) return;

    createAnnotation.mutate({
      ...currentAnnotation,
      range: serializedRange,
    });
  };

  const createAnnotation = trpc.createAnnotation.useMutation({
    onSuccess: (response) => {
      if (!annotationsOpen) {
        setScrollDisabled(true);
        setAnnotationsOpen(true);
        requestAnimationFrame(() => {
          const el = document.getElementById(response.range.startNodeId);
          el?.scrollIntoView({ behavior: "instant", block: "start" });
          setScrollDisabled(false);
        });
      }
      setFocusedId(`${response.publicationId}_${response.id}`);
      setAnnotationSaved(true);
      clearHighlight("selected");
    },
  });

  useHighlightObserver({
    annotations: annotations || [],
    containerRef,
    applyHighlight,
    applyHighlights,
    clearHighlight,
    clearBorder,
    deserializeRange,
    htmlChunks,
    annotationsOpen,
    focusedId,
    updateMenus: updateAnnotationMenuPositions,
  });

  useEffect(() => {
    window.getSelection()?.removeAllRanges();
    requestAnimationFrame(() => {
      applyHighlights(annotations || [], focusedId);
      reapplyHighlightForSnippet();
      updateAnnotationMenuPositions();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ereaderSettings]);

  useEffect(() => {
    if (!pendingHash) return;
    const tryScroll = (attempts = 0) => {
      const el = document.getElementById(pendingHash);
      if (el) {
        if (scrollSnippet) {
          const cleanedSnippet = cleanSnippet(scrollSnippet);
          const range = getRangeFromSnippet(el, cleanedSnippet);
          if (range) {
            range.startContainer.parentElement?.scrollIntoView({
              behavior: "instant",
            });
            applyHighlightForSnippet(cleanedSnippet, pendingHash);
          }
        } else {
          el.scrollIntoView({ behavior: "instant" });
        }
        selectSection(pendingHash, undefined, true);
        setTimeout(() => {
          if (isElementInViewport(el)) {
            setScrollSnippet(undefined);
            setPendingHash(undefined);
            setScrollDisabled(false);
          } else {
            if (attempts < 20) {
              tryScroll(attempts + 1);
            } else {
              setPendingHash(undefined);
              setScrollDisabled(false);
            }
          }
        }, 100);
      } else if (attempts < 20) {
        setTimeout(() => tryScroll(attempts + 1), 100);
      } else {
        setPendingHash(undefined);
        setScrollDisabled(false);
      }
    };
    if (htmlChunks) {
      tryScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingHash, annotationsOpen, htmlChunks]);

  useEffect(() => {
    if (
      pendingHash ||
      scrollDisabled ||
      !scrollSnippet ||
      window.location.hash.slice(1) !== currentSection?.id
    )
      return;
    const el = document.getElementById(currentSection?.id);
    if (!el) return;
    const cleanedSnippet = cleanSnippet(scrollSnippet);

    const range = getRangeFromSnippet(el, cleanedSnippet);
    if (range) {
      range.startContainer.parentElement?.scrollIntoView({
        behavior: "instant",
      });
      applyHighlightForSnippet(cleanedSnippet, currentSection.id);
    }
    setScrollSnippet(undefined);
    setScrollDisabled(false);
  }, [
    applyHighlightForSnippet,
    containerRef,
    currentSection,
    pendingHash,
    scrollDisabled,
    scrollSnippet,
    setScrollDisabled,
    setScrollSnippet,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !publicationSlug) return;
    const footnotes = container.querySelectorAll<HTMLElement>(".footnote");

    const sectionMap: Record<string, HTMLElement[]> = {};

    footnotes.forEach((footnote) => {
      if (
        footnote.getAttribute("data-processed") === ereaderSettings?.footnotes
      )
        return;

      let parent = footnote.parentElement;
      while (
        parent &&
        (!parent.id || !parent.id.includes(`${publicationSlug}-`))
      ) {
        parent = parent.parentElement;
      }

      const sectionId = parent?.id ?? "";
      const chapterKey = extractChapterKey(sectionId) || publicationSlug;
      const group = sectionMap[chapterKey] || [];
      group.push(footnote);
      sectionMap[chapterKey] = group;
    });

    for (const [chapter, notes] of Object.entries(sectionMap)) {
      notes.forEach((note, i) => {
        const footnoteId = `footnote-${chapter}-${i + 1}`;
        const existingContent = note.getAttribute("content");
        const content = existingContent || note.innerHTML;

        note.innerHTML = `<sup>
        <button class="footnote-toggle" aria-label="Show footnote" data-id="${footnoteId}">[${i + 1}]</button>
      </sup>
      <span id="${footnoteId}" class="footnote-content ${ereaderSettings?.footnotes === "inline" ? "" : "hidden"}">
        [${i + 1}] ${content}
      </span>`;
        if (ereaderSettings?.footnotes === "inline") {
          note.setAttribute("data-processed", "inline");
        } else {
          note.setAttribute("data-processed", "clickToShow");
        }

        if (!existingContent) {
          note.setAttribute("content", content);
        }
      });
    }

    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.classList.contains("footnote-toggle")) return;

      const targetId = target.getAttribute("data-id");
      if (!targetId) return;

      const footnote = container.querySelector(`#${CSS.escape(targetId)}`);
      if (!footnote) return;

      footnote.classList.toggle("hidden");
      clearHighlight();
      clearBorder();
      applyHighlights(annotations || [], focusedId);
    };

    container.addEventListener("click", handler);
    return () => container.removeEventListener("click", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    htmlChunks,
    publicationSlug,
    annotations,
    focusedId,
    ereaderSettings?.footnotes,
  ]);

  const onSelectEnd = useCallback(() => {
    if (!contents) return;
    const range = rangeRef.current;
    const rect = range?.getBoundingClientRect();
    const selection = window.getSelection();
    const rangeAt =
      (selection && selection.rangeCount >= 1 && selection.getRangeAt(0)) ||
      null;
    const clonedSelection = rangeAt?.cloneContents();
    const div = document.createElement("div");
    if (clonedSelection) {
      div.appendChild(clonedSelection);
    }
    // Remove any bookmark portal elements captured by the selection
    div.querySelectorAll("[data-bookmark-portal]").forEach((el) => el.remove());
    const highlightedText = div.innerHTML;

    if (
      range &&
      !range.collapsed &&
      rect &&
      selectedChapter &&
      highlightedText
    ) {
      const chapter = getSectionFromContents(contents, selectedChapter);
      setAnnotationPosition(rect);
      setCurrentAnnotation({
        publicationId: publication.id,
        highlightedText,
        range,
        sectionId: selectedChapter,
        sectionTitle: chapter?.label || "",
        noteText: "",
        userId: user?.id,
      });
      setCurrentHighlightMenuOpen(true);
    } else {
      closeCurrentHighlightMenu();
    }
  }, [
    contents,
    selectedChapter,
    closeCurrentHighlightMenu,
    setAnnotationPosition,
    setCurrentAnnotation,
    setCurrentHighlightMenuOpen,
    user,
    publication,
  ]);

  const onSelectEndDebounced = useMemo(
    () => debounce(onSelectEnd, 200),
    [onSelectEnd],
  );

  const onSelectionChange = useCallback(() => {
    if (didTapMenuRef?.current) {
      setTimeout(() => closeCurrentHighlightMenu(), 100);
    } else {
      closeCurrentHighlightMenu();
    }
    if (!publicationSlug || !chapters) return;
    const selected = window.getSelection();

    if (!selected || selected?.rangeCount < 1) return;

    const range = selected?.getRangeAt(0);

    if (range?.collapsed) return;

    const startNodeId = getParentElementWithId(range.startContainer);
    const endNodeId = getParentElementWithId(range.endContainer);
    const startChapterId = extractChapterKey(startNodeId || "");
    const endChapterId = extractChapterKey(endNodeId || "");
    if (startChapterId !== endChapterId) {
      window.getSelection()?.removeAllRanges();
      return;
    }

    rangeRef.current = range;

    if (range) {
      clearHighlightForSnippet();
      return applyHighlight(range, "selected");
    }
  }, [
    publicationSlug,
    chapters,
    applyHighlight,
    clearHighlightForSnippet,
    closeCurrentHighlightMenu,
    didTapMenuRef,
  ]);

  const onSelectStart = useCallback(
    (event: Event) => {
      const target = event.target as Node | null;
      const startNodeId = target
        ? getParentElementWithId(target)
        : publicationSlug;
      const startChapterId = extractChapterKey(startNodeId || "");
      setSelectedChapter(startChapterId || currentSection?.id || undefined);
    },
    [publicationSlug, currentSection?.id],
  );

  useEffect(() => {
    document?.addEventListener("selectstart", onSelectStart);
    document?.addEventListener("selectionchange", onSelectionChange);
    document?.addEventListener("selectionchange", onSelectEndDebounced);
    document.addEventListener("mouseup", onSelectEnd);
    document.addEventListener("pointerup", onSelectEnd);

    return () => {
      document.removeEventListener("selectstart", onSelectStart);
      document?.removeEventListener("selectionchange", onSelectionChange);
      document?.removeEventListener("selectionchange", onSelectEndDebounced);
      document?.removeEventListener("mouseup", onSelectEnd);
      document.removeEventListener("pointerup", onSelectEnd);
    };
  }, [
    onSelectStart,
    onSelectionChange,
    applyHighlight,
    onSelectEndDebounced,
    onSelectEnd,
  ]);

  return (
    <>
      {
        <PublicationWrapper options={pageColourOption}>
          {!htmlChunks ? (
            <LoadingSpinner />
          ) : (
            <PublicationBody options={pageColourOption}>
              <PageWithSettings
                id={id}
                containerRef={containerRef}
                options={pageColourOption}
                settings={ereaderSettings}
              >
                {currentHighlightMenuOpen &&
                  annotationPosition &&
                  containerRef.current && (
                    <AnnotationMenu
                      saveAnnotation={saveAnnotation}
                      annotationPosition={annotationPosition}
                      textContainerRef={containerRef}
                      annotationSaved={annotationSaved}
                      didTapMenuRef={didTapMenuRef}
                    />
                  )}
                {Object.entries(htmlChunks)
                  .sort(([a], [b]) => +a - +b)
                  .map(([position, chunk]) => (
                    <Chunk
                      key={position}
                      position={position}
                      html={chunk}
                      ref={currentChunk === position ? currentChunkRef : null}
                      currentChunk={currentChunk}
                      focusedId={focusedId}
                      annotations={
                        annotations?.filter((ann) => {
                          const chapterId = extractChapterKey(ann.sectionId);
                          if (!chapters || !chapterId) return false;
                          return chapters?.[chapterId] === position;
                        }) || []
                      }
                      annotationPositions={annotationPositions}
                      annotationsOpen={annotationsOpen}
                      clearHighlight={clearHighlight}
                      clearBorder={clearBorder}
                      applyBorder={applyBorder}
                      deserializeRange={deserializeRange}
                      textContainerRef={containerRef}
                      setFocusedId={setFocusedId}
                      pageColour={pageColourOption?.background}
                    />
                  ))}
              </PageWithSettings>
            </PublicationBody>
          )}
        </PublicationWrapper>
      }
      {!isLarge &&
        portalTargets.map((target) => {
          return createPortal(
            <BookmarkButton
              sectionId={target.sectionId}
              sectionLabel={target.sectionLabel}
              annotationsOpen={annotationsOpen}
            />,
            target.container,
            target.sectionId,
          );
        })}
    </>
  );
};
